import { useRef } from "react";
import { PAGE_X } from "../_shared/safeArea";
import { useTrackedScreen } from "../../hooks/useTrackedScreen";
import type { SchemeTokens } from "@shared/types/foyer";
import { nordManifest } from "@shared/schemes/manifests";
import { SchemeProvider } from "../_shared/schemeContext";
import { useScreenFocus } from "../_shared/useFocusGrid";
import { useBackHome } from "../_shared/useBackHome";
import { LauncherIcon } from "../_shared/launcher/icons";
import {
  LauncherPage,
  LauncherCustom,
  LauncherTv,
  useHomeNav,
  useLauncherClock,
  visibleFunctions,
  homeBgLayers,
  useGuestName,
  welcomeLine,
  SchemeSlot,
  launcherSlotStyle,
  type LauncherTheme,
  type LauncherScreen,
} from "../_shared/launcher/kit";
import { useNow } from "../_shared/useNow";
import { offerSlotBanner } from "../_shared/slots/offerSlot";
import type { SchemeProps } from "../registry";

/**
 * Nord - urban design hotel. ARCHETYPE: minimal full-bleed photo with a giant
 * clock bottom-left and a thin, type-led nav strip along the foot. Monochrome
 * bone/ink + one terracotta accent, Space Grotesk / Space Mono.
 */
function nordTheme(t: SchemeTokens): LauncherTheme & { rootBg: string } {
  const e = { ...nordManifest.defaultTokens.extras, ...t.extras };
  return {
    serif: t.fonts.serif ?? t.fonts.sans ?? "",
    sans: t.fonts.sans ?? "",
    mono: t.fonts.mono,
    surface: e.surface,
    card: e.card,
    chip: e.chip,
    line: t.palette.rule ?? "",
    ink: t.palette.ink,
    muted: t.palette.inkDim ?? "",
    accent: t.palette.accent,
    onAccent: e.onAccent,
    phTone: e.phTone,
    phInk: e.phInk,
    photoTone: "dark",
    rootBg: t.palette.bg,
  };
}

export default function NordScheme({ scheme }: SchemeProps) {
  const scopeRef = useRef<HTMLDivElement>(null);
  useScreenFocus(scopeRef);
  const [screen, setScreen] = useTrackedScreen<LauncherScreen>("home");
  useBackHome(() => setScreen("home"));
  const T = nordTheme(scheme.tokens);
  const c = useLauncherClock();
  const guest = useGuestName();
  const { hotelName, city } = scheme.content.branding;
  const hero = scheme.content.images.hero;
  const bgL = homeBgLayers(scheme.content.background?.mode);
  const funcs = visibleFunctions(scheme);
  // The home promo slot, when it holds a live offer (`POS-4`). Resolved here
  // because the dock's focus chain needs to know whether there is a cell to
  // add, and `SchemeSlot` needs to know whether to draw a ring on it - one
  // answer behind both, or the remote and the picture disagree.
  // Read off the scheme directly rather than through `useOfferSlot`: this
  // component body sits OUTSIDE its own `SchemeProvider` (the provider is in
  // the tree it returns), so the context hook has nothing to read here.
  const promoClock = useNow();
  const promoBanner = offerSlotBanner(scheme, "homePromo", promoClock);
  const home = useHomeNav({
    funcs,
    screen,
    go: setScreen,
    scopeRef,
    tvPlayer: scheme.context.tvPlayer,
    headerFirst: true,
    // Last in the chain, and only while there is a live offer: an offer nobody
    // asked for may interrupt the eye, never the remote, so the ring still
    // starts on the dock.
    promo: {
      active: !!promoBanner,
      onPress: () => setScreen("offers"),
    },
  });
  const isHome = screen === "home";
  const cream = T.ink;
  const mono = T.mono || T.sans;

  return (
    <SchemeProvider value={scheme}>
      <div
        ref={scopeRef}
        style={{
          position: "absolute",
          inset: 0,
          background: T.rootBg,
          color: cream,
          overflow: "hidden",
          fontFamily: T.sans,
          outline: "none",
        }}
      >
        {isHome ? (
          <>
            {bgL.photo && (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  backgroundColor: hero ? "#000" : T.phTone,
                  backgroundImage: hero
                    ? `url(${hero})`
                    : `repeating-linear-gradient(135deg, ${T.phInk} 0 1px, transparent 1px 11px)`,
                  backgroundSize: hero ? "cover" : undefined,
                  backgroundPosition: "center",
                }}
              />
            )}
            {bgL.tint && (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  background:
                    "linear-gradient(180deg, rgba(15,13,9,0.55) 0%, rgba(15,13,9,0) 28%, rgba(15,13,9,0) 50%, rgba(15,13,9,0.85) 100%)",
                }}
              />
            )}
          </>
        ) : (
          <div
            style={{ position: "absolute", inset: 0, background: T.surface }}
          />
        )}

        {/* top-left logo + utility (persistent) */}
        <div
          style={{
            position: "absolute",
            top: 56,
            left: PAGE_X,
            display: "flex",
            alignItems: "center",
            gap: 28,
            zIndex: 2,
          }}
        >
          <div
            style={{ fontSize: 28, fontWeight: 700, letterSpacing: "0.02em" }}
          >
            {hotelName}
          </div>
          {/* The property's own city, never a claim about what kind of
              place it is - a hard-coded "design hotel" sat beside every
              name, spa and business hotel alike. No city, no line. */}
          {city && (
            <span
              style={{
                fontFamily: mono,
                fontSize: 13,
                color: T.muted,
                letterSpacing: "0.04em",
              }}
            >
              {city}
            </span>
          )}
        </div>
        <div
          style={{
            position: "absolute",
            top: 56,
            right: PAGE_X,
            display: "flex",
            alignItems: "center",
            gap: 22,
            zIndex: 2,
          }}
        >
          {home.headerIcons.map((name, k) => {
            const on = isHome && home.headerIdx === k;
            return (
              <div
                key={name}
                style={{
                  padding: 6,
                  borderRadius: 9,
                  display: "flex",
                  background: on ? `${T.accent}26` : "transparent",
                  border: on
                    ? `1px solid ${T.accent}`
                    : "1px solid transparent",
                }}
              >
                <LauncherIcon
                  name={name}
                  size={24}
                  color={on ? T.accent : cream}
                />
              </div>
            );
          })}
          <span style={{ width: 1, height: 22, background: T.muted }} />
          <LauncherIcon name="cloud" size={24} color={cream} />
          <span style={{ fontFamily: mono, fontSize: 22 }}>19°</span>
          <span style={{ width: 1, height: 22, background: T.muted }} />
          <span style={{ fontFamily: mono, fontSize: 22 }}>{c.time24}</span>
        </div>

        {/* body */}
        {isHome ? (
          <div style={{ position: "absolute", left: PAGE_X, bottom: 150 }}>
            <div
              style={{
                fontSize: 168,
                fontWeight: 500,
                letterSpacing: "-0.04em",
                lineHeight: 0.86,
              }}
            >
              {c.time24}
            </div>
            <div
              style={{
                fontFamily: mono,
                fontSize: 18,
                letterSpacing: "0.06em",
                color: T.muted,
                marginTop: 14,
              }}
            >
              {c.dateShort.toUpperCase()} -{" "}
              {welcomeLine("WELCOME", guest?.toUpperCase())}
            </div>
          </div>
        ) : screen === "tv" ? (
          // Full-bleed, before LauncherPage: the built-in player carries its
          // own left-hand menu and must own the whole panel, exactly like the
          // vendor app it replaces.
          <LauncherTv theme={T} go={setScreen} scopeRef={scopeRef} />
        ) : screen === "custom" ? (
          <LauncherCustom theme={T} go={setScreen} scopeRef={scopeRef} />
        ) : (
          <div
            style={{
              position: "absolute",
              top: 138,
              left: PAGE_X,
              right: PAGE_X,
              bottom: 40,
            }}
          >
            <LauncherPage
              screen={screen}
              theme={T}
              go={setScreen}
              scopeRef={scopeRef}
            />
          </div>
        )}

        {/* operator promo slot - right side, between top chrome and nav strip */}
        {isHome && (
          <SchemeSlot
            focused={home.promoFocused}
            onPress={() => setScreen("offers")}
            slotId="homePromo"
            style={launcherSlotStyle(T)}
            containerStyle={{
              position: "absolute",
              top: 160,
              right: PAGE_X,
              width: 420,
              zIndex: 2,
            }}
          />
        )}

        {/* bottom nav strip - home only */}
        {isHome && (
          <div
            style={{
              position: "absolute",
              left: PAGE_X,
              right: PAGE_X,
              bottom: 56,
              display: "flex",
              gap: 54,
              alignItems: "flex-end",
              zIndex: 2,
            }}
          >
            {funcs.map((f, i) => {
              const on = home.funcIdx === i;
              return (
                <div
                  key={f.key}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 12,
                    paddingBottom: 12,
                    borderBottom: on
                      ? `2px solid ${T.accent}`
                      : "2px solid transparent",
                  }}
                >
                  <LauncherIcon
                    name={f.icon}
                    size={30}
                    color={on ? T.accent : cream}
                    stroke={1.6}
                  />
                  <span
                    style={{
                      fontFamily: mono,
                      fontSize: 16,
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      color: on ? T.accent : cream,
                    }}
                  >
                    {f.label}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </SchemeProvider>
  );
}
